import React, { useEffect, useState } from 'react';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';

/**
 * Email Compatibility Checker Component
 * 
 * Validates email HTML for compatibility with major email clients:
 * - Gmail (Desktop & Mobile)
 * - Outlook (2007-2021, Office 365)
 * - Apple Mail
 * - Yahoo Mail
 * - Thunderbird
 * 
 * Checks for:
 * - Unsupported CSS properties
 * - Invalid HTML tags
 * - Image issues (missing alt, embedded base64)
 * - Width constraints
 * - Email signature specific rules
 */

const EmailCompatibilityChecker = ({ htmlContent, mode = 'footer' }) => {
    const [issues, setIssues] = useState([]);
    const [score, setScore] = useState(100);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (htmlContent) {
            checkCompatibility(htmlContent);
        }
    }, [htmlContent, mode]);

    const checkCompatibility = (html) => {
        setLoading(true);
        const foundIssues = [];

        // Create a temporary DOM element to parse HTML
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // 1. Check for unsupported CSS properties
        const unsupportedCSSProperties = [
            'position', 'float', 'display: flex', 'display: grid',
            'transform', 'animation', '@keyframes', '@media',
            'box-shadow', 'text-shadow', 'opacity'
        ];

        const styleElements = doc.querySelectorAll('[style]');
        styleElements.forEach(element => {
            const style = element.getAttribute('style');
            unsupportedCSSProperties.forEach(prop => {
                if (style.includes(prop)) {
                    foundIssues.push({
                        type: 'error',
                        category: 'CSS',
                        message: `Unsupported CSS property: "${prop}"`,
                        suggestion: 'Use table-based layouts and inline styles only',
                        element: element.tagName
                    });
                }
            });
        });

        // 2. Check for <style> tags (should use inline only)
        const styleTags = doc.querySelectorAll('style');
        if (styleTags.length > 0) {
            foundIssues.push({
                type: 'warning',
                category: 'CSS',
                message: `Found ${styleTags.length} <style> tag(s)`,
                suggestion: 'All CSS should be inline for maximum compatibility',
                clientsAffected: ['Gmail (clips <style> after 8KB)', 'Outlook']
            });
        }

        // 3. Check for external stylesheets
        const linkTags = doc.querySelectorAll('link[rel="stylesheet"]');
        if (linkTags.length > 0) {
            foundIssues.push({
                type: 'error',
                category: 'CSS',
                message: 'External stylesheets are not supported',
                suggestion: 'Use inline styles only',
                clientsAffected: ['All email clients']
            });
        }

        // 4. Check for JavaScript
        const scriptTags = doc.querySelectorAll('script');
        if (scriptTags.length > 0) {
            foundIssues.push({
                type: 'error',
                category: 'Security',
                message: 'JavaScript is not allowed in emails',
                suggestion: 'Remove all <script> tags',
                clientsAffected: ['All email clients']
            });
        }

        // 5. Check images
        const images = doc.querySelectorAll('img');
        images.forEach((img, index) => {
            // Check for alt text
            if (!img.hasAttribute('alt') || img.getAttribute('alt').trim() === '') {
                foundIssues.push({
                    type: 'warning',
                    category: 'Accessibility',
                    message: `Image ${index + 1} missing alt text`,
                    suggestion: 'Add alt text for accessibility and when images are blocked',
                    clientsAffected: ['Gmail (blocks images by default)']
                });
            }

            // Check for base64 embedded images (bad for signatures)
            const src = img.getAttribute('src');
            if (src && src.startsWith('data:image')) {
                if (mode === 'signature') {
                    foundIssues.push({
                        type: 'error',
                        category: 'Images',
                        message: `Image ${index + 1} uses base64 encoding`,
                        suggestion: 'Use hosted images (HTTP/HTTPS URLs) for email signatures',
                        clientsAffected: ['Outlook', 'Some mobile clients']
                    });
                } else {
                    foundIssues.push({
                        type: 'warning',
                        category: 'Images',
                        message: `Image ${index + 1} uses base64 encoding`,
                        suggestion: 'Hosted images load faster and have better compatibility',
                        clientsAffected: ['Some email clients']
                    });
                }
            }

            // Check image dimensions
            const width = img.getAttribute('width') || img.style.width;
            if (width) {
                const widthNum = parseInt(width);
                if (widthNum > 600) {
                    foundIssues.push({
                        type: 'warning',
                        category: 'Layout',
                        message: `Image ${index + 1} width exceeds 600px`,
                        suggestion: 'Keep email width under 600px for best compatibility',
                        clientsAffected: ['Mobile clients', 'Some desktop clients']
                    });
                }
            }
        });

        // 6. Check container width
        const allElements = doc.querySelectorAll('*');
        allElements.forEach(element => {
            const width = element.getAttribute('width') || element.style.width;
            if (width) {
                const widthNum = parseInt(width);
                if (widthNum > 600 && element.tagName !== 'IMG') {
                    foundIssues.push({
                        type: 'warning',
                        category: 'Layout',
                        message: `${element.tagName} width exceeds 600px`,
                        suggestion: 'Standard email width is 600px',
                        clientsAffected: ['Mobile clients']
                    });
                }
            }
        });

        // 7. Check for forms (not supported)
        const forms = doc.querySelectorAll('form, input, textarea, button[type="submit"]');
        if (forms.length > 0) {
            foundIssues.push({
                type: 'error',
                category: 'HTML',
                message: 'Forms are not supported in emails',
                suggestion: 'Use links to external forms instead',
                clientsAffected: ['All email clients']
            });
        }

        // 8. Check for video/audio
        const mediaElements = doc.querySelectorAll('video, audio, iframe');
        if (mediaElements.length > 0) {
            foundIssues.push({
                type: 'error',
                category: 'HTML',
                message: 'Video, audio, and iframes are not supported',
                suggestion: 'Use thumbnail images linking to external content',
                clientsAffected: ['All email clients']
            });
        }

        // 9. Signature-specific checks
        if (mode === 'signature') {
            // Check for tables (preferred for signatures)
            const tables = doc.querySelectorAll('table');
            if (tables.length === 0) {
                foundIssues.push({
                    type: 'info',
                    category: 'Best Practice',
                    message: 'Consider using tables for layout',
                    suggestion: 'Table-based layouts have the best compatibility for signatures',
                    clientsAffected: ['Outlook 2007-2019']
                });
            }

            // Check signature height
            const bodyHeight = doc.body.scrollHeight;
            if (bodyHeight > 300) {
                foundIssues.push({
                    type: 'warning',
                    category: 'Best Practice',
                    message: 'Signature appears too tall',
                    suggestion: 'Keep signatures concise (under 300px height)',
                    clientsAffected: ['Mobile clients']
                });
            }
        }

        // 10. Check for web fonts
        const fontFamilies = new Set();
        allElements.forEach(element => {
            const style = element.getAttribute('style');
            if (style && style.includes('font-family')) {
                const match = style.match(/font-family:\s*([^;]+)/);
                if (match) {
                    fontFamilies.add(match[1].trim());
                }
            }
        });

        const webSafeFonts = [
            'Arial', 'Helvetica', 'Times New Roman', 'Times', 'Courier New',
            'Courier', 'Verdana', 'Georgia', 'Palatino', 'Garamond',
            'Bookman', 'Comic Sans MS', 'Trebuchet MS', 'Impact', 'sans-serif', 'serif'
        ];

        fontFamilies.forEach(font => {
            const isWebSafe = webSafeFonts.some(safe =>
                font.toLowerCase().includes(safe.toLowerCase())
            );
            if (!isWebSafe && !font.includes('fallback')) {
                foundIssues.push({
                    type: 'warning',
                    category: 'Typography',
                    message: `Custom font detected: ${font}`,
                    suggestion: 'Use web-safe fonts with fallbacks',
                    clientsAffected: ['Outlook', 'Gmail (may not load custom fonts)']
                });
            }
        });

        // Calculate compatibility score
        const errorCount = foundIssues.filter(i => i.type === 'error').length;
        const warningCount = foundIssues.filter(i => i.type === 'warning').length;
        const infoCount = foundIssues.filter(i => i.type === 'info').length;

        const calculatedScore = Math.max(0, 100 - (errorCount * 20) - (warningCount * 5) - (infoCount * 2));

        setIssues(foundIssues);
        setScore(calculatedScore);
        setLoading(false);
    };

    const getScoreColor = () => {
        if (score >= 90) return 'text-green-600';
        if (score >= 70) return 'text-yellow-600';
        if (score >= 50) return 'text-orange-600';
        return 'text-red-600';
    };

    const getScoreBgColor = () => {
        if (score >= 90) return 'bg-green-100';
        if (score >= 70) return 'bg-yellow-100';
        if (score >= 50) return 'bg-orange-100';
        return 'bg-red-100';
    };

    const getIcon = (type) => {
        switch (type) {
            case 'error':
                return <XCircleIcon className="h-5 w-5 text-red-500" />;
            case 'warning':
                return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
            case 'info':
                return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
            default:
                return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
        }
    };

    if (loading) {
        return (
            <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Checking compatibility...</p>
            </div>
        );
    }

    return (
        <div className="email-compatibility-checker">
            {/* Compatibility Score */}
            <div className={`p-4 rounded-lg ${getScoreBgColor()} mb-4`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-medium text-gray-900">Email Compatibility Score</h3>
                        <p className="text-xs text-gray-600 mt-1">
                            {mode === 'signature' ? 'Signature' : 'Footer'} compatibility check
                        </p>
                    </div>
                    <div className={`text-3xl font-bold ${getScoreColor()}`}>
                        {score}%
                    </div>
                </div>

                {score === 100 && (
                    <div className="mt-3 flex items-center text-green-700">
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        <span className="text-sm font-medium">Perfect! No compatibility issues found.</span>
                    </div>
                )}
            </div>

            {/* Issues List */}
            {issues.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-900">
                        Issues Found ({issues.length})
                    </h4>

                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {issues.map((issue, index) => (
                            <div
                                key={index}
                                className="p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                            >
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 mt-0.5">
                                        {getIcon(issue.type)}
                                    </div>
                                    <div className="ml-3 flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium text-gray-900">
                                                {issue.message}
                                            </p>
                                            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                                {issue.category}
                                            </span>
                                        </div>

                                        {issue.suggestion && (
                                            <p className="mt-1 text-xs text-gray-600">
                                                💡 {issue.suggestion}
                                            </p>
                                        )}

                                        {issue.clientsAffected && (
                                            <p className="mt-1 text-xs text-gray-500">
                                                Affects: {issue.clientsAffected.join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Email Client Support Matrix */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="text-xs font-medium text-gray-700 mb-2">Email Client Support</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center">
                        <CheckCircleIcon className={`h-4 w-4 mr-1 ${score >= 80 ? 'text-green-500' : 'text-yellow-500'}`} />
                        <span>Gmail</span>
                    </div>
                    <div className="flex items-center">
                        <CheckCircleIcon className={`h-4 w-4 mr-1 ${score >= 80 ? 'text-green-500' : 'text-yellow-500'}`} />
                        <span>Outlook</span>
                    </div>
                    <div className="flex items-center">
                        <CheckCircleIcon className={`h-4 w-4 mr-1 ${score >= 80 ? 'text-green-500' : 'text-yellow-500'}`} />
                        <span>Apple Mail</span>
                    </div>
                    <div className="flex items-center">
                        <CheckCircleIcon className={`h-4 w-4 mr-1 ${score >= 80 ? 'text-green-500' : 'text-yellow-500'}`} />
                        <span>Yahoo Mail</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailCompatibilityChecker;
